import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { Container } from '@connected/ui/layouts/container';
import { useTranslation } from 'react-i18next';

import {
    InformationLine,
    InformationLineText,
} from '@/components/information-line';
import { useCondition } from '@/config/conditions';

import { useSummaryData } from '../hooks/use-summary-data';

import { IB22Remarks } from './ib22-remarks';
import { ReportCard } from './report-card';

export type InspectionSummaryProps = {
    electricalProjectVersionId: string;
};

export function InspectionSummary({ electricalProjectVersionId }: InspectionSummaryProps) {
    const { t } = useTranslation('re-inspections');
    const canAccessSolarInspection = useCondition('canAccessSolarInspection');
    const { context } = useElectricalProject();

    const { summary, reports } = useSummaryData({ electricalProjectVersionId });

    const selectedReports = reports.filter(
        report => report.InspectionKind && context.selectedInspectionKinds.has(report.InspectionKind),
    );

    const isMultiReport = selectedReports.length > 1;
    const singleReport = selectedReports.length === 1 ? selectedReports[0] : undefined;

    const verdictLabel = (() => {
        if (isMultiReport) {
            const hasFailure = selectedReports.some(r => r.IsFinalVerdictPassed === false);
            if (hasFailure) return 'Voldoet niet';

            const allPassed = selectedReports.every(r => r.IsFinalVerdictPassed === true);
            if (allPassed) return 'Voldoet';

            return 'Nog niet bekend';
        }

        if (summary?.IsFinalVerdictPassed === true) return 'Voldoet';
        if (summary?.IsFinalVerdictPassed === false) return 'Voldoet niet';
        return 'Nog niet bekend';
    })();

    return (
        <>
            <Container>
                <Container.Header>
                    <Container.Title>
                        {t('recap.layout.navigation.recap.label')}
                    </Container.Title>
                </Container.Header>
                <Container.Content>
                    <div className="flex flex-col gap-2">
                        <InformationLine
                            label={InformationLineText(t('recap.form.verdict'))}
                            value={verdictLabel}
                        />
                        <InformationLine
                            label={InformationLineText(
                                t('recap.form.distributionBoxAmount'),
                            )}
                            value={
                                summary?.DistributionBoardCount || 'Nog niet bekend'
                            }
                        />
                        <InformationLine
                            label={InformationLineText(
                                t('recap.form.finalCircuitAmount'),
                            )}
                            value={summary?.FinalCircuitCount || 'Nog niet bekend'}
                        />
                        {canAccessSolarInspection && (
                            <InformationLine
                                label="Aantal omvormers"
                                value={summary?.PVInverterCount || 'Nog niet bekend'}
                            />
                        )}
                        {singleReport && (
                            <>
                                <InformationLine
                                    label={InformationLineText(t('recap.form.nextInspection'))}
                                    value={
                                        singleReport.NextInspectionInYearCount
                                            ? `${singleReport.NextInspectionInYearCount} jaar`
                                            : 'Nog niet bekend'
                                    }
                                />
                                <InformationLine
                                    label={InformationLineText(t('recap.form.remarks'))}
                                    value={(
                                        <IB22Remarks
                                            observations={singleReport.Observations || []}
                                        />
                                    )}
                                />
                            </>
                        )}
                    </div>
                </Container.Content>
            </Container>

            {isMultiReport && selectedReports.map(report => (
                <ReportCard
                    key={report.InspectionKind}
                    report={report}
                />
            ))}
        </>
    );
}
